import type { Tables } from '@biopeak/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase.ts';

export type StoredAnalysis = Tables<'patient_ai_analysis'>;

/**
 * Formato da saída estruturada do modelo.
 *
 * Escrito à mão porque a coluna é `jsonb` e o gerador do Supabase só enxerga
 * `Json` — mesma situação de `PatientOverview`. O schema real que o modelo é
 * obrigado a seguir vive em `supabase/functions/patient-analysis/prompt.ts`;
 * este tipo é a metade da frente e precisa ser mantido junto com aquele.
 */
export interface AnalysisContent {
  resumo: string;
  achados: {
    titulo: string;
    metricas: string[];
    direcao: 'melhora' | 'piora' | 'estavel' | 'instavel';
    observacao: string;
    evidencia: { numeros: number[]; descricao: string };
    confianca: 'alta' | 'media' | 'baixa';
    /**
     * Escrito pela edge function, não pelo modelo: `false` significa que algum
     * número citado não foi encontrado no brief. A interface MOSTRA isso — um
     * achado não verificado misturado aos verificados seria pior que não ter
     * verificação nenhuma, porque daria confiança falsa ao conjunto.
     */
    verificado?: boolean;
  }[];
  atencao: { titulo: string; porque: string }[];
  perguntas_sugeridas: string[];
  qualidade_do_dado: string;
}

/** A análise mais recente já gravada, lida direto da tabela (a RLS filtra). */
export function useLatestAnalysis(patientId: string) {
  return useQuery({
    queryKey: ['patient-analysis', patientId],
    queryFn: async (): Promise<StoredAnalysis | null> => {
      const { data, error } = await supabase
        .from('patient_ai_analysis')
        .select('*')
        .eq('patient_id', patientId)
        .order('generated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export interface AnalysisResult {
  status: 'ok' | 'sem_dado';
  cached?: boolean;
  analysis?: StoredAnalysis;
  message?: string;
}

/**
 * Erro com CÓDIGO, não só mensagem.
 *
 * A tela precisa distinguir "o paciente não autorizou" de "a chave não está
 * configurada" de "o modelo falhou": os três exigem ações diferentes de quem
 * está olhando, e um alerta genérico de erro faria o médico abrir um chamado
 * para algo que ele mesmo resolveria pedindo ao paciente.
 */
export class AnalysisError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = 'AnalysisError';
  }
}

export function useGenerateAnalysis(patientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (options?: { days?: number; force?: boolean }) => {
      const { data, error } = await supabase.functions.invoke<AnalysisResult>(
        'patient-analysis',
        { body: { patientId, days: options?.days ?? 90, force: options?.force ?? false } },
      );

      // `functions.invoke` embrulha respostas não-2xx num FunctionsHttpError e
      // não devolve o corpo. Sem lê-lo, o código de erro que a função tomou o
      // cuidado de mandar se perderia justamente nos casos em que ele importa.
      if (error) {
        const resposta = (error as { context?: Response }).context;
        if (resposta) {
          try {
            const corpo = await resposta.json();
            throw new AnalysisError(
              corpo?.error ?? 'desconhecido',
              corpo?.message ?? corpo?.error ?? error.message,
            );
          } catch (err) {
            if (err instanceof AnalysisError) throw err;
          }
        }
        throw new AnalysisError('desconhecido', error.message);
      }

      return data as AnalysisResult;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['patient-analysis', patientId] });
    },
  });
}
